import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScanFace, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { ScanningState } from "@/hooks/useFaceVerification";

interface FaceIDScannerProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  state: ScanningState;
  error?: string | null;
}

const FaceIDScanner = ({ videoRef, state, error }: FaceIDScannerProps) => {
  const getFeedbackMessage = () => {
    switch (state) {
      case "searching": return "Position your face in the circle";
      case "capturing_1": return "Stay still... Capturing";
      case "capturing_2": return "Almost there... Keep still";
      case "verifying": return "Verifying identity...";
      case "success": return "Identity Verified";
      case "error": return error || "Verification Failed";
      default: return "Ready to scan";
    }
  };

  const ringColor = {
    searching: "border-[hsl(var(--app-gold)/0.3)]",
    capturing_1: "border-[hsl(var(--app-gold))]",
    capturing_2: "border-[hsl(var(--app-gold))]",
    verifying: "border-[hsl(var(--app-blue))]",
    success: "border-[hsl(var(--app-green))]",
    error: "border-[hsl(var(--app-red))]",
    idle: "border-gray-700",
  }[state];

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="relative">
        {/* The circular scanner container */}
        <div className={`relative w-[75vw] h-[75vw] max-w-[280px] max-h-[280px] md:w-72 md:h-72 rounded-full overflow-hidden border-4 ${ringColor} transition-colors duration-500 shadow-[0_0_30px_rgba(0,0,0,0.5)]`}>
          
          {/* Video Feed */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover transition-opacity duration-700 ${state === "idle" ? "opacity-0" : "opacity-100"}`}
          />

          {/* iOS-style Scanning Ring Segments (animated) */}
          <AnimatePresence>
            {(state === "capturing_1" || state === "capturing_2" || state === "searching") && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 pointer-events-none"
              >
                <div className="absolute inset-[-4px] border-4 border-transparent rounded-full border-t-[hsl(var(--app-gold))] animate-spin-slow" />
                <div className="absolute inset-[-4px] border-4 border-transparent rounded-full border-b-[hsl(var(--app-gold))] animate-spin-slow-reverse" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Verification Progress Ring */}
          {state === "verifying" && (
            <div className="absolute inset-[-4px] border-4 border-transparent rounded-full border-l-[hsl(var(--app-blue))] border-r-[hsl(var(--app-blue))] animate-spin" />
          )}

          {/* Success Overlay */}
          <AnimatePresence>
            {state === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 bg-[hsl(var(--app-green)/0.15)] flex items-center justify-center backdrop-blur-[2px]"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 12 }}
                >
                  <CheckCircle2 className="w-20 h-20 text-[hsl(var(--app-green))]" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Overlay */}
          <AnimatePresence>
            {state === "error" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 bg-[hsl(var(--app-red)/0.15)] flex items-center justify-center backdrop-blur-[2px]"
              >
                <motion.div
                  initial={{ rotate: -10, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                >
                  <AlertTriangle className="w-20 h-20 text-[hsl(var(--app-red))]" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Liveness "Scanning Line" */}
          {(state === "capturing_1" || state === "capturing_2") && (
            <motion.div
              initial={{ top: "0%" }}
              animate={{ top: "100%" }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[hsl(var(--app-gold))] to-transparent shadow-[0_0_15px_hsl(var(--app-gold))] z-10"
            />
          )}
        </div>

        {/* Status Badge (bottom) */}
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-20">
          <div className={`px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest uppercase flex items-center gap-2 border shadow-lg ${
            state === "success" ? "bg-[hsl(var(--app-green))] text-white border-[hsl(var(--app-green))]" :
            state === "error" ? "bg-[hsl(var(--app-red))] text-white border-[hsl(var(--app-red))]" :
            "bg-[hsl(var(--app-bg)/0.9)] text-[hsl(var(--app-gold))] border-[hsl(var(--app-gold)/0.3)]"
          }`}>
            {state === "verifying" && <Loader2 className="w-3 h-3 animate-spin" />}
            {state === "searching" && <ScanFace className="w-3 h-3 animate-pulse" />}
            {getFeedbackMessage()}
          </div>
        </div>
      </div>

      {/* Subtext info */}
      <div className="text-center max-w-[240px]">
        {state === "idle" && (
          <p className="text-sm text-[hsl(var(--app-text-muted))]">
            We use advanced AI to verify your identity. Position yourself in a well-lit area.
          </p>
        )}
        {(state === "capturing_1" || state === "capturing_2") && (
          <p className="text-sm text-[hsl(var(--app-gold))] font-medium animate-pulse">
            Keep your face centered and steady
          </p>
        )}
        {state === "error" && (
          <p className="text-xs text-[hsl(var(--app-red))] mt-1">
            {error || "Try sitting in better lighting or removing glasses."}
          </p>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 4s linear infinite;
        }
      `}} />
    </div>
  );
};

export default FaceIDScanner;
